import type { FlowDocument, FlowNodePosition } from "./types";

const COLUMN_WIDTH = 220;
const ROW_HEIGHT = 110;

/**
 * Positions every node for a read-only preview.
 *
 * If every node already carries an editor `position` (docs/plans/06: "Nodes
 * have id/type/config and optional editor position"), those are used as-is
 * — this is what a real designer would have saved, and "Layout changes
 * alone cannot change behavior" cuts both ways: a saved layout must render
 * as saved, not be silently recomputed.
 *
 * None of the three shipped examples carry positions yet, so the common
 * case today is the fallback: a deterministic layered auto-layout. Column =
 * a node's shortest-path distance (in edge hops) from start_node_id, found
 * by BFS; row = discovery order within that column. BFS naturally tolerates
 * the bounded Repeat cycles the schema allows (a back-edge just never
 * revisits an already-layered node), so this needs no separate cycle
 * handling.
 *
 * A node unreachable from start (schema-invalid per semantic rule "no
 * unreachable node", but this function does not itself validate) is placed
 * in one trailing column so it is still visible rather than silently
 * dropped.
 */
export function layoutFlow(doc: FlowDocument): Record<string, FlowNodePosition> {
  if (doc.nodes.length > 0 && doc.nodes.every((node) => node.position)) {
    const positions: Record<string, FlowNodePosition> = {};
    for (const node of doc.nodes) {
      positions[node.id] = node.position as FlowNodePosition;
    }
    return positions;
  }

  const outgoing = new Map<string, string[]>();
  for (const node of doc.nodes) {
    outgoing.set(node.id, []);
  }
  for (const edge of doc.edges) {
    outgoing.get(edge.source)?.push(edge.target);
  }

  const column = new Map<string, number>();
  if (doc.nodes.some((node) => node.id === doc.start_node_id)) {
    column.set(doc.start_node_id, 0);
    const queue = [doc.start_node_id];
    while (queue.length > 0) {
      const current = queue.shift() as string;
      const currentColumn = column.get(current) as number;
      for (const target of outgoing.get(current) ?? []) {
        if (!column.has(target)) {
          column.set(target, currentColumn + 1);
          queue.push(target);
        }
      }
    }
  }

  const unreachableColumn = Math.max(0, ...column.values()) + 1;
  const rowsUsedInColumn = new Map<number, number>();
  const positions: Record<string, FlowNodePosition> = {};
  for (const node of doc.nodes) {
    const nodeColumn = column.get(node.id) ?? unreachableColumn;
    const row = rowsUsedInColumn.get(nodeColumn) ?? 0;
    rowsUsedInColumn.set(nodeColumn, row + 1);
    positions[node.id] = { x: nodeColumn * COLUMN_WIDTH, y: row * ROW_HEIGHT };
  }
  return positions;
}
